if (!customElements.get('how-to-apply-swipe')) {
  customElements.define(
    'how-to-apply-swipe',
    class HowToApplySwipe extends HTMLElement {
      constructor() {
        super();

        this.track = null;
        this.viewport = null;
        this.realSlides = [];
        this.trackSlides = [];
        this.nextButtons = [];
        this.statusNode = null;

        this.logicalIndex = 0;
        this.trackIndex = 1;
        this.transitioning = false;
        this.dragging = false;
        this.dragStartX = 0;
        this.dragDeltaX = 0;
        this.wheelLock = false;

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.videos = [];
        this.videoListeners = [];

        this.handleNextClick = this.handleNextClick.bind(this);
        this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handleMediaChange = this.handleMediaChange.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector('.how-to-apply-swipe__track');
        this.viewport = this.querySelector('.how-to-apply-swipe__viewport');
        this.statusNode = this.querySelector('[data-slide-status]');

        if (!this.track || !this.viewport) return;

        this.realSlides = Array.from(this.track.querySelectorAll('.how-to-apply-swipe__slide'));

        if (this.realSlides.length < 2) return;

        this.setupInfiniteTrack();
        this.bindEvents();
        this.setupVideoPlayback();
        this.snapToTrackIndex(this.trackIndex, false);
        this.updateA11yState();
      }

      disconnectedCallback() {
        this.unbindEvents();

        this.videoListeners.forEach(({ element, eventName, listener }) => {
          element.removeEventListener(eventName, listener);
        });
        this.videoListeners = [];

        this.mobileMediaQuery.removeEventListener('change', this.handleMediaChange);
        this.prefersReducedMotion.removeEventListener('change', this.handleMediaChange);

        this.pauseAllVideos();
      }

      setupInfiniteTrack() {
        const firstClone = this.realSlides[0].cloneNode(true);
        const lastClone = this.realSlides[this.realSlides.length - 1].cloneNode(true);

        firstClone.classList.add('is-clone');
        firstClone.setAttribute('aria-hidden', 'true');

        lastClone.classList.add('is-clone');
        lastClone.setAttribute('aria-hidden', 'true');

        this.track.prepend(lastClone);
        this.track.append(firstClone);

        this.trackSlides = Array.from(this.track.querySelectorAll('.how-to-apply-swipe__slide'));
      }

      bindEvents() {
        this.nextButtons = Array.from(this.querySelectorAll('[data-next]'));

        this.nextButtons.forEach((button) => {
          button.addEventListener('click', this.handleNextClick);
        });

        this.track.addEventListener('transitionend', this.handleTransitionEnd);

        this.viewport.addEventListener('pointerdown', this.handlePointerDown);
        this.viewport.addEventListener('pointermove', this.handlePointerMove);
        this.viewport.addEventListener('pointerup', this.handlePointerUp);
        this.viewport.addEventListener('pointercancel', this.handlePointerUp);
        this.viewport.addEventListener('lostpointercapture', this.handlePointerUp);
        this.viewport.addEventListener('wheel', this.handleWheel, { passive: false });

        this.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('resize', this.handleResize);

        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }

      unbindEvents() {
        this.nextButtons.forEach((button) => {
          button.removeEventListener('click', this.handleNextClick);
        });

        if (this.track) {
          this.track.removeEventListener('transitionend', this.handleTransitionEnd);
        }

        if (this.viewport) {
          this.viewport.removeEventListener('pointerdown', this.handlePointerDown);
          this.viewport.removeEventListener('pointermove', this.handlePointerMove);
          this.viewport.removeEventListener('pointerup', this.handlePointerUp);
          this.viewport.removeEventListener('pointercancel', this.handlePointerUp);
          this.viewport.removeEventListener('lostpointercapture', this.handlePointerUp);
          this.viewport.removeEventListener('wheel', this.handleWheel);
        }

        this.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('resize', this.handleResize);

        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }

      handleNextClick(event) {
        if (event.currentTarget.closest('.is-clone')) return;
        this.goToTrackIndex(this.trackIndex + 1, true);
      }

      handleTransitionEnd(event) {
        if (event.target !== this.track || event.propertyName !== 'transform') return;

        if (this.trackIndex === 0) {
          this.trackIndex = this.realSlides.length;
          this.snapToTrackIndex(this.trackIndex, false);
        } else if (this.trackIndex === this.realSlides.length + 1) {
          this.trackIndex = 1;
          this.snapToTrackIndex(this.trackIndex, false);
        }

        this.transitioning = false;
        this.updateA11yState();
      }

      handlePointerDown(event) {
        if (this.transitioning) return;
        if (event.button !== 0) return;
        if (event.target.closest('button, a, input, select, textarea, label')) return;

        this.dragging = true;
        this.dragStartX = event.clientX;
        this.dragDeltaX = 0;

        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture(event.pointerId);

        this.setTrackTransition(false);
      }

      handlePointerMove(event) {
        if (!this.dragging) return;

        this.dragDeltaX = event.clientX - this.dragStartX;
        this.applyDragTransform(this.dragDeltaX);
      }

      handlePointerUp(event) {
        if (!this.dragging) return;

        if (this.viewport.hasPointerCapture(event.pointerId)) {
          this.viewport.releasePointerCapture(event.pointerId);
        }

        this.viewport.classList.remove('is-dragging');
        this.dragging = false;

        const threshold = Math.max(40, this.viewport.clientWidth * 0.12);

        if (Math.abs(this.dragDeltaX) > threshold) {
          if (this.dragDeltaX < 0) {
            this.goToTrackIndex(this.trackIndex + 1, true);
          } else {
            this.goToTrackIndex(this.trackIndex - 1, true);
          }
        } else {
          this.snapToTrackIndex(this.trackIndex, !this.prefersReducedMotion.matches);
        }

        this.dragDeltaX = 0;
      }

      handleKeydown(event) {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          this.goToTrackIndex(this.trackIndex + 1, true);
          return;
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.goToTrackIndex(this.trackIndex - 1, true);
        }
      }

      handleBlockSelect(event) {
        const sectionId = this.dataset.sectionId;
        const detailSectionId = event.detail?.sectionId;
        const blockId = event.detail?.blockId;

        if (!sectionId || !detailSectionId || sectionId !== detailSectionId || !blockId) return;

        const targetIndex = this.realSlides.findIndex((slide) => slide.dataset.blockId === blockId);

        if (targetIndex === -1) return;

        this.logicalIndex = targetIndex;
        this.trackIndex = targetIndex + 1;
        this.snapToTrackIndex(this.trackIndex, false);
        this.updateA11yState();
      }

      handleResize() {
        this.snapToTrackIndex(this.trackIndex, false);
      }

      handleWheel(event) {
        if (this.transitioning || this.dragging || this.wheelLock) return;

        const horizontalIntent = Math.abs(event.deltaX) > Math.abs(event.deltaY);
        if (!horizontalIntent) return;

        const threshold = 16;
        if (Math.abs(event.deltaX) < threshold) return;

        event.preventDefault();

        if (event.deltaX > 0) {
          this.goToTrackIndex(this.trackIndex + 1, true);
        } else {
          this.goToTrackIndex(this.trackIndex - 1, true);
        }

        this.wheelLock = true;
        window.setTimeout(() => {
          this.wheelLock = false;
        }, 450);
      }

      goToTrackIndex(nextTrackIndex, animate) {
        if (this.transitioning) return;

        this.trackIndex = nextTrackIndex;
        this.transitioning = animate && !this.prefersReducedMotion.matches;

        this.snapToTrackIndex(this.trackIndex, animate && !this.prefersReducedMotion.matches);

        if (!this.transitioning) {
          if (this.trackIndex === 0) {
            this.trackIndex = this.realSlides.length;
            this.snapToTrackIndex(this.trackIndex, false);
          } else if (this.trackIndex === this.realSlides.length + 1) {
            this.trackIndex = 1;
            this.snapToTrackIndex(this.trackIndex, false);
          }

          this.updateA11yState();
        }
      }

      snapToTrackIndex(trackIndex, animate) {
        this.setTrackTransition(animate);
        const xPercent = trackIndex * -100;
        this.track.style.transform = `translate3d(${xPercent}%, 0, 0)`;
      }

      applyDragTransform(deltaX) {
        const basePercent = this.trackIndex * -100;
        this.track.style.transform = `translate3d(calc(${basePercent}% + ${deltaX}px), 0, 0)`;
      }

      setTrackTransition(enabled) {
        if (enabled) {
          this.track.style.transition = 'transform 450ms cubic-bezier(0.22, 0.61, 0.36, 1)';
        } else {
          this.track.style.transition = 'none';
        }
      }

      updateA11yState() {
        this.logicalIndex = (this.trackIndex - 1 + this.realSlides.length) % this.realSlides.length;

        this.trackSlides.forEach((slide, index) => {
          const isVisibleTrackSlide = index === this.trackIndex;
          const isClone = slide.classList.contains('is-clone');
          const isActiveRealSlide = isVisibleTrackSlide && !isClone;

          if (isActiveRealSlide) {
            slide.removeAttribute('inert');
          } else {
            slide.setAttribute('inert', '');
          }

          const slideButtons = Array.from(slide.querySelectorAll('[data-next]'));
          slideButtons.forEach((button) => {
            button.tabIndex = isActiveRealSlide ? 0 : -1;
          });

          slide.setAttribute('aria-hidden', isActiveRealSlide ? 'false' : 'true');
        });

        this.updateVideoPlayback();

        if (!this.statusNode) return;

        this.statusNode.textContent = `Step ${this.logicalIndex + 1} of ${this.realSlides.length}`;
      }

      setupVideoPlayback() {
        this.videos = Array.from(this.querySelectorAll('.how-to-apply-swipe__video'));

        if (!this.videos.length) return;

        this.videos.forEach((video) => {
          video.muted = true;
          video.controls = false;
        });

        // Desktop: hover the media shell to play, leave to pause (matches the
        // non-swipe How To Apply section). Clones are included so hovering a
        // wrapped edge slide behaves the same.
        const shells = Array.from(this.querySelectorAll('.how-to-apply-swipe__media-shell'));

        shells.forEach((shell) => {
          const video = shell.querySelector('.how-to-apply-swipe__video');
          if (!video) return;

          const handleEnter = () => {
            if (this.mobileMediaQuery.matches || this.prefersReducedMotion.matches) return;
            this.playVideo(video);
          };

          const handleLeave = () => {
            if (this.mobileMediaQuery.matches) return;
            this.pauseVideo(video);
          };

          this.addTrackedListener(shell, 'mouseenter', handleEnter);
          this.addTrackedListener(shell, 'mouseleave', handleLeave);
        });

        this.mobileMediaQuery.addEventListener('change', this.handleMediaChange);
        this.prefersReducedMotion.addEventListener('change', this.handleMediaChange);

        this.updateVideoPlayback();
      }

      handleMediaChange() {
        this.updateVideoPlayback();
      }

      updateVideoPlayback() {
        if (!this.videos.length) return;

        // Reduced motion pauses everything; desktop is hover-driven, so nothing
        // autoplays there either.
        if (this.prefersReducedMotion.matches || !this.mobileMediaQuery.matches) {
          this.pauseAllVideos();
          return;
        }

        // Mobile: autoplay the active slide's visible video(s), pause the rest.
        this.pauseAllVideos();

        const activeSlide = this.trackSlides[this.trackIndex];
        if (!activeSlide) return;

        const activeVideos = Array.from(activeSlide.querySelectorAll('.how-to-apply-swipe__video'));
        activeVideos.forEach((video) => {
          if (video.offsetParent === null) return;
          this.playVideo(video);
        });
      }

      playVideo(video) {
        video.play().catch(() => {});
      }

      pauseVideo(video) {
        video.pause();
      }

      pauseAllVideos() {
        this.videos.forEach((video) => video.pause());
      }

      addTrackedListener(element, eventName, listener) {
        element.addEventListener(eventName, listener);
        this.videoListeners.push({ element, eventName, listener });
      }
    }
  );
}
